import { fetchWithAuth } from "../FetchWithAuth";
import { url } from "../url";

const BASE = `${url}/api/ai`;

async function parseJson(response) {
  return response.json().catch(() => ({}));
}

export async function getModels() {
  const response = await fetchWithAuth(`${BASE}/models`, { method: "GET" });
  return parseJson(response);
}

export async function getConversations() {
  const response = await fetchWithAuth(`${BASE}/conversations`, { method: "GET" });
  return parseJson(response);
}

export async function createConversation(payload = {}) {
  const response = await fetchWithAuth(`${BASE}/conversations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return parseJson(response);
}

export async function renameConversation(conversationId, title) {
  const response = await fetchWithAuth(`${BASE}/conversations/${conversationId}`, {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
  return parseJson(response);
}

export async function deleteConversation(conversationId) {
  const response = await fetchWithAuth(`${BASE}/conversations/${conversationId}`, {
    method: "DELETE",
  });
  return parseJson(response);
}

export async function getConversationMessages(conversationId, page = 1, perPage = 50) {
  const response = await fetchWithAuth(
    `${BASE}/conversations/${conversationId}/messages?page=${page}&per_page=${perPage}`,
    { method: "GET" }
  );
  return parseJson(response);
}

export async function uploadAttachment({ file, conversationId }) {
  const formData = new FormData();
  formData.append("file", file);
  if (conversationId) {
    formData.append("conversation_id", String(conversationId));
  }
  const response = await fetchWithAuth(`${BASE}/chat/upload-file`, {
    method: "POST",
    body: formData,
  });
  if (!response) return;
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || "Upload failed");
  }
  return data;
}

export async function streamChat({ conversationId, message, model, attachmentIds = [], parentMessageId, onEvent }) {
  const response = await fetchWithAuth(`${BASE}/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversation_id: conversationId,
      message,
      model,
      attachment_ids: attachmentIds,
      parent_message_id: parentMessageId,
    }),
  });

  if (!response) return; // 401 handled by fetchWithAuth (redirect)
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Streaming request failed");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() || "";

    chunks.forEach((chunk) => {
      const line = chunk
        .split("\n")
        .find((part) => part.startsWith("data: "));
      if (!line) return;
      const payload = line.replace("data: ", "");
      try {
        const parsed = JSON.parse(payload);
        if (onEvent) onEvent(parsed);
      } catch (error) {
        // Ignore malformed SSE fragments.
      }
    });
  }
}

export async function regenerateMessage(messageId, model) {
  const response = await fetchWithAuth(`${BASE}/messages/${messageId}/regenerate`, {
    method: "POST",
    body: JSON.stringify({ model }),
  });
  return parseJson(response);
}

export async function continueMessage(messageId, model) {
  const response = await fetchWithAuth(`${BASE}/messages/${messageId}/continue`, {
    method: "POST",
    body: JSON.stringify({ model }),
  });
  return parseJson(response);
}

export async function editMessage(messageId, content, model) {
  const response = await fetchWithAuth(`${BASE}/messages/${messageId}`, {
    method: "PATCH",
    body: JSON.stringify({ content, model }),
  });
  return parseJson(response);
}

export async function reactToMessage(messageId, reaction) {
  const response = await fetchWithAuth(`${BASE}/messages/${messageId}/reaction`, {
    method: "PATCH",
    body: JSON.stringify({ reaction }),
  });
  return parseJson(response);
}

export async function getMyUsage() {
  const response = await fetchWithAuth(`${BASE}/usage/me`, { method: "GET" });
  return parseJson(response);
}

export async function getUsageSummary(days = 30, topUsersPage = 1, topUsersPageSize = 10) {
  const params = new URLSearchParams({
    days: String(days),
    top_users_page: String(topUsersPage),
    top_users_page_size: String(topUsersPageSize),
  });
  const response = await fetchWithAuth(`${BASE}/usage/summary?${params}`, { method: "GET" });
  return parseJson(response);
}

export async function getUsageLimits(page = 1, pageSize = 20) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  const response = await fetchWithAuth(`${BASE}/usage/limits?${params}`, { method: "GET" });
  return parseJson(response);
}

export async function updateUsageLimit(userId, payload) {
  const response = await fetchWithAuth(`${BASE}/usage/limits/${userId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return parseJson(response);
}
